import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

const faqs = [
  {
    question: "How much does digital marketing cost?",
    answer:
      "Pricing depends on services, campaign scope, and ad spend. SEO packages start from affordable monthly retainers, while full-service marketing with PPC and social media is customized to your budget. Contact us for a free quote.",
  },
  {
    question: "How long before I see results?",
    answer:
      "PPC and social ads can show results within days. SEO typically takes 3–6 months for significant organic growth. We set realistic timelines and provide monthly progress reports so you always know where things stand.",
  },
  {
    question: "Do you manage Google Ads and Meta Ads?",
    answer:
      "Yes. We manage Google Search, Display, Shopping, and YouTube ads, as well as Facebook and Instagram campaigns. We handle setup, optimization, A/B testing, and detailed performance reporting.",
  },
  {
    question: "Can you help with local SEO?",
    answer:
      "Absolutely. We optimize Google Business Profile, build local citations, manage reviews, and create location-targeted content to help you rank in local search and Google Maps.",
  },
  {
    question: "Will I get regular reports?",
    answer:
      "Yes. You receive transparent monthly reports covering traffic, leads, conversions, ad spend, ROI, and actionable recommendations. We also provide access to live dashboards when requested.",
  },
  {
    question: "Do you work with small businesses?",
    answer:
      "We work with startups, SMEs, and enterprises alike. Our packages are flexible and scalable — whether you need a focused SEO campaign or a full digital marketing strategy.",
  },
];

const DmFaq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <AnimatedSection as="section" className="dm-section" aria-labelledby="dm-faq-title">
      <div className="dm-container dm-faq__wrap">
        <SectionHeader
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          text="Everything you need to know about our digital marketing services."
        />

        <div className="dm-faq__list">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={faq.question}
                className={`dm-faq__item${isOpen ? " dm-faq__item--open" : ""}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <button
                  type="button"
                  className="dm-faq__trigger"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} aria-hidden="true" />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="dm-faq__answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </AnimatedSection>
  );
};

export default DmFaq;
